<script setup>
import { ref, watch, computed } from 'vue'
import { useAppStore } from '@/stores/app'
import { useMapStore } from '@/stores/map'
import { useDataStore } from '@/stores/data'
import BasePage from './BasePage.vue'
import { h } from 'vue'

const appStore = useAppStore()
const mapStore = useMapStore()
const dataStore = useDataStore()
const permissionType = ref('consents')
const permissions = computed(() => appStore.permissions)
const currentPermission = computed(() => appStore.permissions[permissionType.value])
let notificationInstance = null
const permissionTypes = ['consents', 'notification', 'bluetooth', 'location_wheninuse', 'location_always', 'motion']
const permissionItems = {
	consents: {
		title: 'Samtykke til databehandling',
		tableText: 'Samtykke',
		buttonText: 'Samtykker',
		description: 'Denne appen samler dine reisevaner basert på data om posisjon og bevegelse fra telefonen din. Reisevanene dine behandles og benyttes til å forstå reisevaner i befolkningen. <br/><br/>Du må gi samtykke for å fortsette. Samtykket kan trekkes tilbake av deg inne i appen.',
		icon: 'fas fa-handshake',
		required: true,
	},
	notification: {
		title: 'Varslinger',
		tableText: 'Push',
		buttonText: 'Varslinger',
		warningText: 'Appen mangler tilgang til <strong>Varslinger</strong> og vil derfor ikke varsle deg når du har registrerte reiser for gjennomgang. Om ønskelig kan du endre manuelt i app innstillingene.',
		description: 'For å kunne gi deg beskjed når du har registrerte reiser som trenger gjennomgang trenger appen tilgang til varslinger.',
		icon: 'fas fa-bell',
		prompt: 'Vil du godkjenne tilgangen til varslinger?',
		required: false,
	},
	bluetooth: {
		title: 'Bluetooth',
		tableText: 'Bluetooth',
		buttonText: 'Bluetooth',
		warningText: 'Appen mangler tilgang til <strong>Bluetooth</strong> og vil ikke fungere som forventet. Du må selv endre manuelt i app innstillingene.',
		description: 'For å kunne gjenkjenne kjøretøy i offentlig transport som sender identifiserende signaler trenger appen tilgang til bluetooth.',
		note: 'Du må velge <strong>Tillat</strong> for at appen skal fungere som forventet.',
		icon: 'fa-brands fa-bluetooth',
		prompt: 'Vil du godkjenne tilgangen til Bluetooth?',
		required: true,
	},
	location_wheninuse: {
		title: 'Stedstilgang',
		tableText: 'Sted',
		buttonText: 'Lokasjon nå',
		warningText: 'Appen mangler tilgang til <strong>Sted</strong> og vil ikke fungere som forventet. Du må selv endre manuelt i app innstillingene.',
		description: 'For å kunne registrere reiser trenger appen tilgang til stedstjenester mens den er i bruk.',
		note: 'Du må velge <strong>Tillat mens appen er i bruk</strong> for å kunne delta med denne appen.',
		icon: 'fas fa-location-dot',
		prompt: 'Vil du godkjenne tilgangen til stedstjenester?',
		required: true,
	},
	location_always: {
		title: 'Stedstilgang alltid',
		tableText: 'Reiser',
		buttonText: 'Lokasjon alltid',
		warningText: 'Appen mangler tilgang til <strong>Stedstilgang alltid</strong> og vil ikke fungere som forventet. Du må selv endre manuelt i app innstillingene.',
		description: 'For å kunne registrere alle reisene dine helt automatisk trenger appen tilgang til dine posisjoner i bakgrunnen.',
		note: 'Du må velge <strong>Bytt til tillat alltid</strong> for å kunne delta med denne appen.',
		icon: 'fas fa-route',
		prompt: 'Vil du godkjenne tilgangen dine posisjoner når du reiser?',
		required: true,
	},
	motion: {
		title: 'Bevegelse og kondisjon',
		tableText: 'Bevegelse',
		buttonText: 'Bevegelse',
		warningText: 'Appen mangler tilgang til <strong>Bevegelse og kondisjon</strong> og vil ikke fungere som forventet. Du må selv endre manuelt i app innstillingene.',
		description: 'Ved å gi tilgang til endringer i bevegelse kan appen lettere forstå hvordan du beveger deg, til fots, på sykkel eller med motorisert transport.',
		icon: 'fas fa-person-walking',
		note: 'Du må velge <strong>Tillat</strong> for for å kunne delta med denne appen.',
		prompt: 'Vil du godkjenne tilgang til dine bevegelser?',
		required: true,
	},
	completed: {
		title: 'Fullført',
		tableText: 'Fullført',
		buttonText: 'Fortsett',
		description: 'Alle nødvendige tillatelser er nå gitt og appen kan begynne å samle informasjon for å lage reisedagbok.',
		icon: 'fas fa-check-circle',
	},
}

const nextButtonText = computed(() => {
	const nextIndex = permissionTypes.indexOf(permissionType.value) + 1
	if (nextIndex < permissionTypes.length) {
		return permissionItems[permissionTypes[nextIndex]].tableText
	}
	return 'Fortsett'
})

const backButtonText = computed(() => {
	if (permissionType.value === 'consents') {
		return 'Tilbake'
	} else {
		const prevIndex = permissionTypes.indexOf(permissionType.value) - 1
		if (prevIndex >= 0) {
			return permissionItems[permissionTypes[prevIndex]].tableText
		}
	}
	return ''
})

const showPermissionsPrompt = ref(false)
const permissionsDialogTitle = ref('Gi tilgang til appen')

const handlePromptOk = async (value) => {
	permissions.value[permissionType.value] = true
	showPermissionsPrompt.value = false
}
const handlePromptCancel = () => {
	permissions.value[permissionType.value] = false
	showPermissionsPrompt.value = false
}

const grantConsents = async () => {
	// Log the consent approval event
	appStore.events.trackConsentApproved()

	console.log('Granting consents')
	await appStore.grantConsents()
	console.log('Granting consents done')
	setTimeout(() => {
		setCurrentPermissionType()
	}, 50)
}

const requestPermission = async () => {
	console.warn('PermissionPage: requestPermission:', permissionType.value)
	try {
		// If we try to request LOCATION_ALWAYS before LOCATION_WHENINUSE, then change permissionType to LOCATION_WHENINUSE, since we need to ask for LOCATION_WHENINUSE  before LOCATION_ALWAYS
		if (permissionType.value === 'location_always' && appStore.permissions.location_wheninuse < 1) {
			permissionType.value = 'location_wheninuse'
		}

		if (permissions[permissionType.value] == 1) {
			nextStep()
		} else {
			var status = 'not_determined'
			if (appStore.platform === 'web') {
				// Web platform
				status = await appStore.requestPermission(permissionType.value.toUpperCase())
			} else {
				// Native app
				status = await appStore.requestPermission(kettle.permissions[permissionType.value.toUpperCase()])
				console.warn('PermissionPage: requestPermission status', status)
			}

			// Update the permission status
			permissions.value[permissionType.value] = status

			// Fly to default location if permission is granted
			if (permissionType.value == kettle.permissions.LOCATION_WHENINUSE && status === 1) {
				var locationOslo = { latitude: 59.9139, longitude: 10.7522 }
				mapStore.flyToLocation(locationOslo, 4, 3000)
			}
		}
	} catch (error) {
		console.error('PermissionPage: error', error)
	}
}

const prevStep = () => {
	const currentIndex = permissionTypes.indexOf(permissionType.value)
	if (currentIndex > 0) {
		console.warn('PermissionPage: prevStep', currentIndex, permissionType.value)
		permissionType.value = permissionTypes[currentIndex - 1]
	} else {
		appStore.setPage('onboarding')
	}
}

const nextStep = async () => {
	const currentIndex = permissionTypes.indexOf(permissionType.value)
	if (currentIndex < permissionTypes.length - 1) {
		console.warn('PermissionPage: nextStep', currentIndex, permissionType.value)
		permissionType.value = permissionTypes[currentIndex + 1]
	} else {
		await kettle.set('onboarded_permissions', 'true')
		appStore.setPage('onboarding')
	}
}

const openAppSettings = async () => {
	if (dataStore.platform === 'web') {
		if (window.self !== window.top) {
			alert('Lenking til appinnstillinger er bare tilgjengelig i appen, men du kan simulere dette ved å endre tillatelser i sidemenyen.')
			window.parent?.openSidebar()
		}
	} else {
		kettle.openUrl('app-settings:')
	}
}

const setCurrentPermissionType = async () => {
	var currentType = 'completed'
	if (appStore.permissions.motion < 1) {
		currentType = 'motion'
	}
	if (appStore.permissions.location_always < 1) {
		currentType = 'location_always'
	}
	if (appStore.permissions.location_wheninuse < 1) {
		currentType = 'location_wheninuse'
	}
	if (appStore.permissions.bluetooth < 1) {
		currentType = 'bluetooth'
	}
	if (appStore.permissions.notification == 0) {
		currentType = 'notification'
	}
	if (!appStore.hasNeededConsents()) {
		currentType = 'consents'
	}
	permissionType.value = currentType
	if (currentType == 'completed' && !appStore.onboardedPermissions) {
		appStore.onboardedPermissions = true
		kettle.set('onboarded_permissions', 'true')
		console.warn('setCurrentPermissionType', currentType, 'was', permissionType.value)
	}
}

const getIconClass = (type) => {
	var success = appStore.permissions[type] > 0
	var error = appStore.permissions[type] < 0
	if (success) {
		return 'fas fa-circle-check'
	} else if (error) {
		return 'fas fa-circle-xmark'
	} else {
		return permissionItems[type].icon
	}
}

const init = () => {
	setTimeout(() => {
		appStore.bottomSheet.setHeightLarge(300, 'PermissionsPage init')

		// Save a timestamp when the permissions page is started
		dataStore.permissionStartedDate = new Date().toISOString()
		kettle.set('permission_started_date', dataStore.permissionStartedDate)
	}, 300)
}

const postPermissions = () => {
	if (dataStore.platform === 'web') {
		// Create a clean copy of permissions
		const sanitizedPermissions = JSON.parse(JSON.stringify(appStore.permissions))

		// Post message immediately - no need for setTimeout in most cases
		window.parent.postMessage({ type: 'permissions', data: sanitizedPermissions }, '*')

		// Update directly in parent window
		if (window.self !== window.top) {
			window.parent.updatePermissions(sanitizedPermissions)
		}
	}
}

const continueClick = () => {
	if (appStore.onboardedSurvey) {
		appStore.bottomSheet.setHeightHidden(500, 'PermissionsPage', false)
		dataStore.loadAndProcessData(true)
	} else {
		appStore.setPage('onboarding')
	}
	appStore.startTracking()

	// Store the timestamp when the permissions were completed
	dataStore.permissionCompletedDate = new Date().toISOString()
	kettle.set('permission_completed_date', dataStore.permissionCompletedDate)

	// Log the permissions approval event
	appStore.events.trackPermissionsApproved()
}

const openPrivacyPolicy = () => {
	kettle.openUrl('http://opinion.no/rvu/privacypolicy')
}

watch(
	() => appStore.permissions,
	(newValue, oldValue) => {
		if (notificationInstance) notificationInstance.close()
		// Add a small delay before showing the next permission type in UI
		setTimeout(() => {
			setCurrentPermissionType()
			postPermissions()
		}, 100)
	},
	{ deep: true },
)

// Watch specifically for location_wheninuse permission changes
watch(
	() => appStore.permissions.location_wheninuse,
	(newValue, oldValue) => {
		if (!appStore.onboardedPermissions) {
			if (oldValue !== newValue && newValue > 0) {
				dataStore.flyToUserLocation(10)
			}
		}
	},
)

watch(
	() => appStore.permissions.location_always,
	(newValue, oldValue) => {
		if (!appStore.onboardedPermissions) {
			if (oldValue !== newValue && newValue > 0) {
				dataStore.flyToUserLocation(14)
			}
		}
	},
)

watch(
	() => appStore.consents,
	() => {
		setCurrentPermissionType()
	},
)

watch(permissionType, (type) => {
	console.log('Permission type changed to:', type, appStore.permissions[type])
	if (appStore.permissions[type] < 0) {
		if (appStore.currentPage != 'permissions') {
			appStore.setPage('permissions')
		}
		if (notificationInstance) notificationInstance.close()
		notificationInstance = appStore.toast({
			message: permissionItems[type].warningText,
			dangerouslyUseHTMLString: true,
			duration: 30000,
			icon: h('i', { class: permissionItems[type].icon, style: 'font-size: 18px; color: var(--color-danger);' }),
		})
	}
})
</script>

<template>
	<BasePage name="permissions" @active="init" title="Gi appen nødvendige tillatelser">
		<div class="flex flex-col justify-between items-center h-full">
			<div class="text-center flex flex-grow flex-col justify-center items-center" style="flex: 1">
				<div class="permission" :key="permissionType" :class="{ granted: permissions[permissionType] > 0 }">
					<i :class="permissionItems[permissionType].icon"></i>
					<h2>{{ permissionItems[permissionType].title }}</h2>
					<div class="description" v-html="permissionItems[permissionType].description"></div>
					<div class="note" v-if="permissionItems[permissionType].note" v-html="permissionItems[permissionType].note"></div>
				</div>
			</div>

			<template v-if="permissionType == 'consents'">
				<div class="w-80 pb-6">
					<div class="button white border full" @click="openPrivacyPolicy">
						Personvernerklæring
						<i class="fa-solid fa-up-right-from-square"></i>
					</div>
				</div>
			</template>

			<div class="permission-table" :class="{ hidden: permissionType == 'consents' }">
				<div v-for="type in permissionTypes.filter((s) => s !== 'consents')" :key="type" @click="permissionType = type" :class="[type, { active: permissionType === type, success: permissions[type] > 0, error: permissions[type] < 0 }]">
					<i :class="[getIconClass(type)]"></i>
					<div>{{ permissionItems[type].tableText }}</div>
				</div>
			</div>
		</div>

		<template #footer>
			<div class="button" @click="prevStep" v-if="permissionType != 'completed'">
				<i class="fas fa-chevron-left"></i>
				{{ backButtonText }}
			</div>
			<div class="button blue full" @click="requestPermission" v-if="currentPermission == 0">
				Gi tilgang
				<i class="fas fa-check"></i>
			</div>
			<div class="button blue full" @click="grantConsents" v-if="permissionType == 'consents'">
				Gi samtykke
				<i class="fas fa-check"></i>
			</div>
			<div class="button blue full" @click="nextStep" v-if="currentPermission == 1">
				{{ nextButtonText }}
				<i class="fas fa-chevron-right"></i>
			</div>
			<div class="button orange full" @click="openAppSettings" v-if="currentPermission == -1">
				Endre manuelt
				<i class="fas fa-up-right-from-square"></i>
			</div>
			<div class="button full green" @click="continueClick()" v-if="permissionType == 'completed'">
				Fortsett
				<i class="fas fa-chevron-right"></i>
			</div>
		</template>
	</BasePage>
</template>

<style>
.page.permissions .body {
	padding: 0px;
	display: flex;
}

.page.permissions .body .inner {
	padding: 0px;
}

.warning {
	padding: 1rem;
	border: 2px solid var(--color-danger);
	color: black;
	font-weight: 400;
	border-radius: 1rem;
	margin-left: 1rem;
	margin-right: 1rem;
	margin-bottom: 1rem;
	font-size: 0.88rem;
}

.permission {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: space-between;
	font-weight: 400;
	font-size: 1.0625rem;
	padding: 1rem;
	padding-bottom: 1.5rem;
	border-radius: 1rem;
	line-height: 1.75rem;
	color: var(--color-blue);
	gap: 0.5rem;
	overflow: visible;
}

/* .permission.granted,
.permission.granted .fas,
.permission.granted .fa-brands {
	color: var(--color-success);
} */

.permission i {
	display: block;
}

.permission h2 {
	font-weight: 400;
	font-size: 1.5rem;
	margin: 0px;
	margin-bottom: 1rem;
	animation: slideFromLeft 0.8s ease-out forwards;
	transition: all 0.5s ease-out;
}

.permission .description {
	text-align: center;
	color: black;
	animation: scaleIn 0.5s ease-out forwards;
}

.permission .note {
	margin-top: 1rem;
	color: var(--color-blue);
	animation: slideFromBottom 0.5s ease-out forwards 0.3s;
	font-weight: 500;
	width: 75vw;
	margin-left: auto;
	margin-right: auto;
	font-size: 0.875rem;
	line-height: 1.125rem;
	text-align: center;
	color: black;
	opacity: 0;
}

.permission-icon {
	height: 6.5rem;
	width: 6.5rem;
	color: var(--color-blue);
	animation: slideFromRight 0.8s cubic-bezier(0, 0.405, 0, 1.285) forwards;
	transition: all 0.5s ease-out;
}

.permission .fas,
.permission .fa-brands {
	font-size: 6rem;
	line-height: 6rem;
	color: var(--color-blue);
	animation: slideFromRight 0.8s cubic-bezier(0, 0.405, 0, 1.285) forwards;
	transition: all 0.5s ease-out;
}

.permission-table {
	margin: 1.5rem;
	margin-left: 1.5rem;
	margin-right: 1.5rem;
	/* margin-bottom: 0.5rem; */
	/* box-sizing: content-box; */
	display: flex;
	justify-content: center;
	align-content: space-between;
	overflow: hidden;
	--color: #aaa;
	border-radius: 1rem;
	border: none;
	box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
	min-height: fit-content;
	width: calc(100% - 3rem);
	background-color: white;
	flex-shrink: 0;
	transition: all 0.5s ease-out;
}

.permission-table.hidden {
	opacity: 0.5;
	transform: scale(0);
	max-height: 0px;
}

.permission-table > div {
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 2.5rem;
	flex-direction: column;
	font-size: 0.8125rem;
	font-weight: 400;
	gap: 0.25rem;
	padding: 0.5rem;
	padding-top: 0.75rem;
	padding-bottom: 0.5rem;
	border-bottom: 3px solid transparent;
	width: 100%;
}

.permission-table > div.active {
	border-bottom: 3px solid var(--color);
	--color: var(--color-blue);
}

.table-icon {
	width: 1rem;
	height: 1rem;
	min-height: 1rem;
	min-width: 1rem;
	fill: var(--color);
}

.permission-table div.success {
	--color: var(--color-success);
}

.permission-table div.error {
	--color: var(--color-danger);
}

.permission-table div.notification.error {
	--color: var(--color-warning);
}

.permission-table i {
	font-size: 1rem;
	color: var(--color);
}

@keyframes slideFromBottom {
	from {
		opacity: 0;
		transform: translateY(150px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes scaleIn {
	from {
		opacity: 0;
		transform: scale(0) translateY(50px);
	}
	to {
		opacity: 1;
		transform: scale(1) translateY(0px);
	}
}

@keyframes slideFromLeft {
	from {
		opacity: 0;
		transform: translateX(-100vw);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

@keyframes slideFromRight {
	from {
		opacity: 0;
		transform: translateX(100vw);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}
</style>
